// The universal state model (DESIGN_SYSTEM §9.1). Every data-bearing component and screen renders
// one of these; "N/A" must be justified in the component.

/** States of a data region (a page, card, table or panel). */
export type DataState =
  | "loading"
  | "empty"
  | "error"
  | "success"
  | "ready"
  | "permission-denied"
  | "plan-locked"
  | "pending-activation"
  | "offline";

/** Who must act before a pending-activation feature can go live. */
export type ActivationActor = "vendor" | "platform" | "third-party";

export interface ActivationStep {
  label: string;
  actor: ActivationActor;
  done: boolean;
}

/**
 * Why a control can't be used. Controls stay focusable (`aria-disabled`) so the reason is reachable
 * by keyboard and screen readers; the 40%-opacity disabled look is not used.
 */
export type Availability =
  | { kind: "enabled" }
  | { kind: "disabled"; reason: string }
  | { kind: "permission-denied"; reason: string }
  | { kind: "plan-locked"; reason: string; plan: string }
  | { kind: "pending-activation"; reason: string };

export const ENABLED: Availability = { kind: "enabled" };

export function isBlocked(
  a: Availability | undefined,
): a is Exclude<Availability, { kind: "enabled" }> {
  return a !== undefined && a.kind !== "enabled";
}

/** Status tones (§3.5), shared by orders, payments, couriers, jobs and vendor lifecycle. */
export type Tone = "neutral" | "action" | "transit" | "success" | "warning" | "danger";
